/**
 * Network Status Indicator Component
 *
 * Displays a MUI Snackbar notification when the app goes offline,
 * and shows a brief "back online" message when connectivity is restored.
 */

import { useState, useEffect } from 'react';
import { Snackbar, Alert, Chip, Box } from '@mui/material';
import WifiOffIcon from '@mui/icons-material/WifiOff';
import WifiIcon from '@mui/icons-material/Wifi';
import CloudQueueIcon from '@mui/icons-material/CloudQueue';
import { useNetworkStatus } from '../hooks/useNetworkStatus';

const NetworkStatusIndicator = () => {
  const { isOnline, queuedActionCount } = useNetworkStatus();
  const [showOnlineSnackbar, setShowOnlineSnackbar] = useState(false);
  const [wasOffline, setWasOffline] = useState(false);

  useEffect(() => {
    if (!isOnline) {
      setWasOffline(true);
    } else if (wasOffline) {
      // Show "back online" notification briefly
      setShowOnlineSnackbar(true);
      setWasOffline(false);
    }
  }, [isOnline, wasOffline]);

  const handleCloseOnlineSnackbar = () => {
    setShowOnlineSnackbar(false);
  };

  return (
    <>
      {/* Persistent offline indicator */}
      <Snackbar open={!isOnline} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert
          severity="warning"
          icon={<WifiOffIcon />}
          sx={{ width: '100%', alignItems: 'center' }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            You are offline. Some features may be unavailable.
            {queuedActionCount > 0 && (
              <Chip
                icon={<CloudQueueIcon />}
                label={`${queuedActionCount} pending`}
                size="small"
                color="warning"
                variant="outlined"
              />
            )}
          </Box>
        </Alert>
      </Snackbar>

      {/* Brief "back online" notification */}
      <Snackbar
        open={showOnlineSnackbar}
        autoHideDuration={3000}
        onClose={handleCloseOnlineSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity="success"
          icon={<WifiIcon />}
          onClose={handleCloseOnlineSnackbar}
          sx={{ width: '100%' }}
        >
          Connection restored. You are back online.
        </Alert>
      </Snackbar>
    </>
  );
};

export default NetworkStatusIndicator;
